import TextInput from "../../../../shared/components/TextInput/TextInput";
import { useAuth } from "../../../../shared/providers/AuthProvider/AuthProvider";
import {
    Box,
    Button,
    Flex,
    Text,
    Stack,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { useMemo } from 'react';
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import InlineErrorInput from "../../../../shared/components/InlineErrorInput/InlineErrorInput";
import { FormControl, Checkbox, FormErrorMessage } from '@chakra-ui/react';

const RegisterPage = () => {
    const { register, user, error, errorCode } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation("default");

    const validationSchema = useMemo(() => Yup.object({
        email: Yup.string()
            .email(t('form.email.message'))
            .required(t('form.email.required')),
        password: Yup.string()
            .min(8, t('form.password.length'))
            .required(t('form.password.required'))
            .matches(/[a-z]/, t('form.password.lowercase'))
            .matches(/[A-Z]/, t('form.password.uppercase'))
            .matches(/\d/, t('form.password.digit'))
            .matches(/[@$!%*?&#^()_+\-=[\]{};':"\\|,.<>/?]/, t('form.password.special')),
        password_repeat: Yup.string()
            .min(8, t('form.password.length'))
            .required(t('form.password.repeat.required'))
            .oneOf([Yup.ref('password'), null], t('form.password.repeat.match')),
        agreementTerms: Yup.boolean().oneOf([true], t('register.agreements.required')),
        agreementDpa: Yup.boolean().oneOf([true], t('register.agreements.required')),
        agreementTruth: Yup.boolean().oneOf([true], t('register.agreements.required')),
        agreementNewsletter: Yup.boolean(),
        agreementCommercial: Yup.boolean(),
        agreementMarketing: Yup.boolean(),
    }), [t]);

    if (user) {
        return <Navigate to={"/"} replace />
    }

    const handleSubmit = (values, { setSubmitting }) => {
        const { email, password, password_repeat, ...agreements } = values;
        register(email, password, agreements);
    }

    const handleNavigate = (route) => {
        navigate(route);
    }

    return (
        <Flex
            bgColor="#F8F5F4"
            bgSize="cover" bgPosition="center"
            w="100%" minH={{ base: 'calc(100vh - 64px)', md: 'calc(100vh - 72px)' }}
            align="center" justify="center"
            px={{ base: 4, sm: 6 }}>

            <Box w="full" maxW="640px" bg="white" border="1px solid #ECE4E4" borderRadius="22px" boxShadow="0 1px 2px rgba(14,26,23,0.04), 0 16px 40px rgba(14,26,23,0.07)" p={{ base: '24px', md: '40px' }} my={{ base: '24px', md: '56px' }} display="flex" gap={'28px'} flexDirection="column" alignItems="stretch">
                <Stack spacing={2} align="flex-start">
                    <Text className="login-title">
                        {t('register.title')}
                    </Text>
                    <Text className="login-message"
                        lineHeight={{ base: '16px', sm: 'sm' }}
                        fontSize={{ base: 20, sm: 24 }}
                    >
                        {t('register.message')}
                    </Text>
                </Stack>

                <Box maxW="3xl" w="full" bg="transparent" gap={'48px'}>
                    <Formik
                        initialValues={{
                            email: '', password: '', password_repeat: '',
                            agreementTerms: false, agreementDpa: false, agreementTruth: false,
                            agreementNewsletter: false, agreementCommercial: false, agreementMarketing: false
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        validateOnChange={true}
                        validateOnBlur={true}
                        validateOnMount={true}
                    >
                        {({ isSubmitting, errors, touched, values, isValid, setValues }) => (
                            <Form>
                                <Flex flexDirection={"column"} gap={'16px'}>
                                    <InlineErrorInput
                                        label={t('form.email.label')}
                                        name="email"
                                        type="email"
                                        placeholder={t('form.email.placeholder')}
                                        value={values.email}
                                        errors={errors.email}
                                        touched={touched.email}
                                        backgroundColor="white"
                                    />
                                    <InlineErrorInput
                                        label={t('form.password.label')}
                                        name="password"
                                        type="password"
                                        placeholder={t('form.password.placeholder')}
                                        value={values.password}
                                        errors={errors.password}
                                        touched={touched.password}
                                        backgroundColor="white"
                                    />
                                    <InlineErrorInput
                                        label={t('form.password.repeat.label')}
                                        name="password_repeat"
                                        type="password"
                                        placeholder={t('form.password.placeholder')}
                                        value={values.password_repeat}
                                        errors={errors.password_repeat}
                                        touched={touched.password_repeat}
                                        backgroundColor="white"
                                    />
                                </Flex>

                                <Flex flexDirection={"column"} gap="12px" mt={6} mb={4} textAlign="left">
                                    <Checkbox
                                        onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            setValues({
                                                ...values,
                                                agreementTerms: isChecked,
                                                agreementDpa: isChecked,
                                                agreementTruth: isChecked,
                                                agreementNewsletter: isChecked,
                                                agreementCommercial: isChecked,
                                                agreementMarketing: isChecked,
                                            });
                                        }}
                                        isChecked={values.agreementTerms && values.agreementDpa && values.agreementTruth && values.agreementNewsletter && values.agreementCommercial && values.agreementMarketing}
                                        fontWeight="600"
                                        alignItems="flex-start"
                                        colorScheme="teal"
                                    >
                                        <Text fontSize="14px" mt="-1" color="gray.700">{t('register.agreements.all')}</Text>
                                    </Checkbox>

                                    <Field name="agreementTerms">
                                        {({ field, form }) => (
                                            <FormControl isInvalid={form.errors.agreementTerms && form.touched.agreementTerms}>
                                                <Checkbox {...field} isChecked={field.value} alignItems="flex-start" colorScheme="teal">
                                                    <Text fontSize="14px" mt="-1" color="gray.700">{t('register.agreements.terms')} *</Text>
                                                </Checkbox>
                                                <FormErrorMessage>{form.errors.agreementTerms}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name="agreementDpa">
                                        {({ field, form }) => (
                                            <FormControl isInvalid={form.errors.agreementDpa && form.touched.agreementDpa}>
                                                <Checkbox {...field} isChecked={field.value} alignItems="flex-start" colorScheme="teal">
                                                    <Text fontSize="14px" mt="-1" color="gray.700">{t('register.agreements.dpa')} *</Text>
                                                </Checkbox>
                                                <FormErrorMessage>{form.errors.agreementDpa}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name="agreementTruth">
                                        {({ field, form }) => (
                                            <FormControl isInvalid={form.errors.agreementTruth && form.touched.agreementTruth}>
                                                <Checkbox {...field} isChecked={field.value} alignItems="flex-start" colorScheme="teal">
                                                    <Text fontSize="14px" mt="-1" color="gray.700">{t('register.agreements.truth')} *</Text>
                                                </Checkbox>
                                                <FormErrorMessage>{form.errors.agreementTruth}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name="agreementNewsletter">
                                        {({ field }) => (
                                            <FormControl>
                                                <Checkbox {...field} isChecked={field.value} alignItems="flex-start" colorScheme="teal">
                                                    <Text fontSize="14px" mt="-1" color="gray.700">{t('register.agreements.newsletter')}</Text>
                                                </Checkbox>
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name="agreementCommercial">
                                        {({ field }) => (
                                            <FormControl>
                                                <Checkbox {...field} isChecked={field.value} alignItems="flex-start" colorScheme="teal">
                                                    <Text fontSize="14px" mt="-1" color="gray.700">{t('register.agreements.commercial')}</Text>
                                                </Checkbox>
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name="agreementMarketing">
                                        {({ field }) => (
                                            <FormControl>
                                                <Checkbox {...field} isChecked={field.value} alignItems="flex-start" colorScheme="teal">
                                                    <Text fontSize="14px" mt="-1" color="gray.700">{t('register.agreements.marketing')}</Text>
                                                </Checkbox>
                                            </FormControl>
                                        )}
                                    </Field>
                                </Flex>

                                {errorCode && (
                                    <Text color="red.500" textAlign="center" mt={4}>
                                        {t(errorCode)}
                                    </Text>
                                )
                                    ||
                                    error && (
                                        <Text color="red.500" textAlign="center" mt={4}>
                                            {error}
                                        </Text>
                                    )}

                                <Box display="flex" flexDirection="column" alignItems="center" mt={8}>
                                    <Button
                                        className="login-button"
                                        display={"flex"}
                                        type="submit"
                                        justifySelf="center"
                                        isLoading={isSubmitting && !error}
                                        gap={'10px'}
                                        bg="#7A1F35"
                                        _hover={{ bg: "#5C1628" }}
                                        h={'59px'}
                                        w={'170px'}
                                        borderRadius={'12px'}
                                        isDisabled={!isValid || isSubmitting}
                                    >
                                        <Text className="login-button-label">
                                            {t('register.button')}
                                        </Text>
                                        <svg width="8" height="10" viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M0 0H3.58481L8 5L3.58481 10H0L4.43544 5L0 0Z" fill="white" />
                                        </svg>
                                    </Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
                <Text className="login-register-message">
                    {t('register.login.message')}
                    <a href="/login" className="login-register-button">
                        {t('login.button')}
                    </a>
                </Text>
            </Box>
        </Flex>
    )
}

export default RegisterPage;
